import { ComponentType } from 'react';
import PropTypes from 'prop-types';
import { connect, ConnectedProps } from 'react-redux';
import userDataApi from './api';
import * as redux from './redux';
import UserDataPropType from './propType';
import { makeErrorFromApi } from 'lib/error';
import { compose, lifecycle, mapProps, setDisplayName } from 'recompose';
import { setCountry } from 'lib/tracking';
import {
  withErrorHandler,
  WithErrorHandlerProps,
} from 'data/error/withErrorHandler';

/**
 * @deprecated
 */
export const WithUserDataPropType = {
  isLoadingUserData: PropTypes.bool.isRequired,
  userData: UserDataPropType,
  loadUserData: PropTypes.func.isRequired,
};

export type WithUserDataProps = {
  isLoadingUserData: boolean;
  userData: redux.UserData | null;
};

export const LOAD_USER_DATA_ERROR_TYPE = 'LOAD_USER_DATA_ERROR_TYPE';

export const loadUserData =
  (dispatch: ConnectedProps<typeof connector>['dispatch']) => async () => {
    dispatch(redux.loadUserData());
    try {
      const data = await userDataApi.loadUserData();
      setCountry(data.country);
      dispatch(redux.setUserData(data));
    } catch (error: any) {
      // Since the SubLandingPage also works outside the authenticated session, it is necessary to resolve the promise as empty string.
      setCountry('');
      dispatch(redux.setUserData(null));
      throw makeErrorFromApi(LOAD_USER_DATA_ERROR_TYPE, error);
    }
  };

type MapInnerProps = Omit<
  ConnectedProps<typeof connector>,
  'isLoading' | 'data' | 'dispatch'
> &
  WithErrorHandlerProps & {
    loadUserData: () => Promise<void>;
    isLoadingUserData: ConnectedProps<typeof connector>['isLoading'];
    userData: ConnectedProps<typeof connector>['data'];
  };

export const withUserData = compose<
  WithUserDataProps,
  ConnectedProps<typeof connector>
>(
  setDisplayName('withUser'),
  withErrorHandler,
  mapProps<
    MapInnerProps,
    ConnectedProps<typeof connector> & WithErrorHandlerProps
  >(({ isLoading, data, dispatch, ...props }) => ({
    ...props,
    loadUserData: loadUserData(dispatch),
    isLoadingUserData: isLoading,
    userData: data,
  })),
  lifecycle<MapInnerProps, {}>({
    componentDidMount() {
      this.props.loadUserData().catch(this.props.setError);
    },
  }),
);

const connector = connect(redux.selector);

const withUserDataHoc = (WrappedComponent: ComponentType<WithUserDataProps>) =>
  connector(withUserData(WrappedComponent));

export default withUserDataHoc;
